"use client";

import { ProductItem } from "@/types/product-types";
import Image from "next/image";
import Link from "next/link";
import { useLocale } from "@/contexts/LocaleContext";

export default function ProductCard({
  title,
  description,
  image,
  badges,
  href = "#",
}: ProductItem) {
  const { t } = useLocale();

  return (
    <Link
      href={href}
      className="group flex h-full flex-col items-stretch overflow-hidden rounded-2xl border border-slate-200/80 bg-white text-center shadow-[0_8px_24px_rgba(15,35,65,0.05)] transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-1 hover:border-blue-200 hover:shadow-[0_16px_36px_rgba(15,35,65,0.11)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 active:translate-y-0"
    >
      <div className="relative aspect-4/3 w-full overflow-hidden border-b border-slate-100 bg-white">
        <Image
          src={image}
          alt={title}
          fill
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw"
          className="object-contain transition-transform duration-500 ease-out group-hover:scale-[1.035]"
        />
      </div>

      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <h3 className="line-clamp-2 min-h-12 text-base font-semibold leading-snug text-slate-900 sm:min-h-14 sm:text-lg">
          {title}
        </h3>

        {badges && (
          <div className="mt-1.5 flex items-center justify-center gap-2">
            {badges.map((b) => (
              <span
                key={b}
                className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600 sm:text-xs"
              >
                {b}
              </span>
            ))}
          </div>
        )}

        <p className="mt-2 line-clamp-3 flex-1 text-[13px] leading-5 text-slate-600 sm:text-sm">
          {description}
        </p>

        <div className="mt-4 border-t border-slate-100 pt-3 text-left">
          <div className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-blue-700 sm:text-sm">
            {t("product.learnMore")}
            <span aria-hidden="true" className="transition-transform duration-200 group-hover:translate-x-1">→</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
